import { ApplicationRef, ErrorHandler, Inject, NgModule } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, NavigationError, NavigationStart, Router, RouteReuseStrategy, RouterModule } from '@angular/router'
import { APP_BASE_HREF } from '@angular/common'
import { LocationUpgradeModule } from '@angular/common/upgrade'
import { HttpClientModule, HttpClientJsonpModule } from '@angular/common/http'
import { UpgradeModule } from '@angular/upgrade/static'
import { FormsModule } from '@angular/forms'
import { NgbModal, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap'

import { RouteLoggerService, UtilCommonModule } from 'common/util/module'
import { APIModule } from 'api/module'
import { LocaleModule } from 'common/locale/module'
import { WebStorageModule } from 'ngx-store'
import { MomentModule } from 'ngx-moment'
import { ToastrModule } from 'ngx-toastr'
import { UtilPickersModule } from 'common/util.pickers/module'
import { UtilReleaseNotesModule } from 'common/util.releaseNotes/module'
import { UtilFileConflictModule } from 'common/util.fileConflict/module'
import { UtilElementsClientModule } from 'common/util.elementsClient/module'
import { MediaModule } from 'media/module'
import { StorageModule } from 'storage/module'
import { TasksModule } from 'tasks/module'
import { MainModule } from 'main/module'
import { TapeModule } from 'ltfs/module'
import { ClickModule } from 'click/module'
import { MediaSharingModule } from 'media.sharing/module'
import { MediaPermissionsModule } from 'media.permissions'
import { MediaCustomFieldsModule } from 'media.customFields'
import { MediaWorkflowModule } from 'media.workflow/module'
import { HelpModule } from 'help'
import { StatusModule } from 'status/module'
import { PanelModule, PanelRootComponent } from 'panel/module'
import { AngularJSModule, LazyLoaderService } from 'ng1'

import { AppRootComponent } from './components/appRoot.component'
import { AngularJSComponent } from '../ng1/components/angularJS.component'
import { FourOhFourView } from './components/404View.component'
import { AuthRootComponent } from './components/authRoot.component'
import { BOOTSTRAP_DATA, LAZY_LOADER } from 'api/providers'

import { BootstrapData } from 'elements-sdk'
import { NavigationErrorViewComponent } from './components/navigationErrorView.component'
import { AngularErrorHandler } from './services/errorHandler.service'
import { Subject } from 'rxjs'
import { SystemModule } from '../system/module'

export class CustomReuseStrategy extends BaseRouteReuseStrategy {
  shouldDetach (route: ActivatedRouteSnapshot): boolean {
    if (route.data['detachRoute'] === true) {
      return true
    }
    return super.shouldDetach(route)
  }

  shouldReuseRoute (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (curr.data['reuseRoute'] !== true) {
      return false
    }
    return super.shouldReuseRoute(future, curr)
  }
}

const navigationError$ = new Subject<NavigationError>()

@NgModule({
  imports: [
    RouterModule.forRoot(
      [
        {
          path: 'file-manager',
          loadChildren: () => import(
            /* webpackChunkName: "app.storage.filemanager" */
            'storage.filemanager/module'
          ),
        },
        {
          path: 'system/volumes',
          loadChildren: () => import(
            /* webpackChunkName: "app.system.volumes" */
            'system.volumes'
          ),
        },
        {
          path: 'auth',
          loadChildren: () => import(
            /* webpackChunkName: "app.main" */
            'auth'
          ),
        },
        // {
        //   path: 'media',
        //   loadChildren: () => import(
        //     /* webpackChunkName: "app.media" */
        //     'media/module'
        //   ),
        // }
        // TODO lazy load once all /media routes are migrated
        ...MediaModule.ROUTES,
        {
          path: 'not-found',
          component: FourOhFourView,
        },
        {
          path: 'navigation-error',
          component: NavigationErrorViewComponent,
        },
        ...AngularJSComponent.ROUTES,
        {
          path: '**',
          pathMatch: 'full',
          component: FourOhFourView,
        },
      ],
      {
        onSameUrlNavigation: 'reload',
        errorHandler: (err: NavigationError) => {
          navigationError$.next(err)
          return true
        },
      },
    ),
    LocationUpgradeModule.config({ useHash: false }),
    MediaModule, // TOOD remove once all /media routes are migrated

    BrowserModule,
    BrowserAnimationsModule,
    UpgradeModule,
    UtilCommonModule,
    UtilPickersModule,
    UtilReleaseNotesModule,
    UtilFileConflictModule,
    UtilElementsClientModule,
    MediaModule,
    MediaPermissionsModule,
    MediaCustomFieldsModule,
    MediaSharingModule,
    MediaWorkflowModule,
    APIModule,
    StorageModule,
    SystemModule,
    TasksModule,
    MainModule,
    TapeModule,
    ClickModule,
    HelpModule,
    StatusModule,
    PanelModule,
    AngularJSModule,

    NgbPopoverModule,
    FormsModule,

    HttpClientModule,
    HttpClientJsonpModule,
    LocaleModule.forRoot(),
    WebStorageModule.forRoot(),
    MomentModule.forRoot(),
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
    }),
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: LAZY_LOADER, useClass: LazyLoaderService },
    { provide: ErrorHandler, useClass: AngularErrorHandler },
  ],
  declarations: [
    AppRootComponent,
    AuthRootComponent,
    NavigationErrorViewComponent,
  ],
})
export class AppModule {
  constructor (
    private upgrade: UpgradeModule,
    private appRef: ApplicationRef,
    private ngbModal: NgbModal,
    private router: Router,
    private routeLogger: RouteLoggerService,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
  ) { }

  ngDoBootstrap () {
    // Restore window.requestAnimationFrame temporarily so that angular-animate uses the original function
    (window as any)['requestAnimationFrame'] = window[window['Zone'].__symbol__('requestAnimationFrame')]

    if (document.querySelector('app-root')) {
      this.appRef.bootstrap(AppRootComponent)
    } else if (document.querySelector('auth-root')) {
      this.appRef.bootstrap(AuthRootComponent)
    } else if (document.querySelector('panel-root')) {
      this.appRef.bootstrap(PanelRootComponent)
    }

    let lastURL = location.pathname
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        lastURL = event.url
      }
    })

    navigationError$.subscribe((err: NavigationError) => {
      this.routeLogger.lastError = err
      history.pushState(null, '', lastURL)
      this.router.navigate(['/navigation-error'], { skipLocationChange: true })
    })
  }
}

export { LazyLoaderService } from '../ng1/services/lazyLoader.service'
